import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import Spinner from "../components/Spinner";
import {
  loadFaceModels,
  recognizeFacesInImage,
} from "../utils/faceRecognition";

const CreateProduct = () => {
  const [local, setLocal] = useState("");
  const [priceInCents, setPriceInCents] = useState("");
  const [evento, setEvento] = useState("");
  const [tags, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Lista de atletas carregados
  const [atletas, setAtletas] = useState([]);
  const [filteredAtletas, setFilteredAtletas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // Atletas selecionados como padrão (caso queira marcar em todas)
  const [selectedAtletas, setSelectedAtletas] = useState([]);

  // Itens de arquivo com metadados de reconhecimento facial
  const [fileItems, setFileItems] = useState([]);
  const [activeFileIndex, setActiveFileIndex] = useState(0);

  // Estados de IA e Upload
  const [isAnalyzingIA, setIsAnalyzingIA] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0, text: "" });
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [successList, setSuccessList] = useState([]);
  const [errorList, setErrorList] = useState([]);

  // Busca de atleta no painel de desambiguação
  const [disambigSearchTerm, setDisambigSearchTerm] = useState("");

  const token = localStorage.getItem("token");
  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const imgFullRef = useRef(null);
  const createdUrlsRef = useRef([]);

  // Contagem de atletas com biometria facial cadastrada
  const athletesWithFaces = useMemo(() => {
    return atletas.filter(
      (a) =>
        Array.isArray(a.fotosReferencia) &&
        a.fotosReferencia.some((f) => Array.isArray(f.descriptor) && f.descriptor.length === 128)
    );
  }, [atletas]);

  // Cleanup de object URLs APENAS ao desmontar o componente
  useEffect(() => {
    return () => {
      createdUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // ignora erro
        }
      });
      createdUrlsRef.current = [];
    };
  }, []);

  // Consultar lista de atletas
  useEffect(() => {
    const fetchAtletas = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas`,
          config
        );
        const data = response.data?.data || [];
        setAtletas(data);
        setFilteredAtletas(data);
      } catch (error) {
        console.error("Erro ao buscar atletas:", error);
      }
    };
    fetchAtletas();
  }, [config]);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchTerm(query);
    const filtered = atletas
      .filter((atleta) => atleta.nome.toLowerCase().includes(query))
      .sort((a, b) => a.nome.localeCompare(b.nome));
    setFilteredAtletas(filtered);
  };

  const handleToggleAtleta = (atleta) => {
    setSelectedAtletas((prev) => {
      const exists = prev.some((a) => a._id === atleta._id);
      if (exists) {
        return prev.filter((a) => a._id !== atleta._id);
      } else {
        return [...prev, { _id: atleta._id, nome: atleta.nome }];
      }
    });
  };

  // Executa o Reconhecimento Facial com IA em lote no navegador
  const runRecognitionOnItems = async (itemsList) => {
    const targets = itemsList || fileItems;
    if (targets.length === 0) {
      enqueueSnackbar("Selecione fotos antes de iniciar o reconhecimento facial.", {
        variant: "warning",
      });
      return;
    }

    if (athletesWithFaces.length === 0) {
      enqueueSnackbar(
        "Nenhum atleta possui fotos de referência com biometria facial cadastradas. Acesse 'Editar Atleta' para adicionar fotos de rosto.",
        { variant: "warning", autoHideDuration: 6000 }
      );
      return;
    }

    setIsAnalyzingIA(true);
    setAnalysisProgress({
      current: 0,
      total: targets.length,
      text: "Inicializando modelos neurais no navegador...",
    });

    try {
      await loadFaceModels();

      const updatedItems = [...targets];
      let totalRecognizedCount = 0;
      let totalFacesCount = 0;

      for (let i = 0; i < updatedItems.length; i++) {
        const item = updatedItems[i];
        setAnalysisProgress({
          current: i + 1,
          total: updatedItems.length,
          text: `Analisando foto ${i + 1}/${updatedItems.length} (${item.name})...`,
        });

        try {
          const result = await recognizeFacesInImage(item.file, athletesWithFaces);
          item.totalFaces = result.totalFaces;
          item.unknownFaces = result.unknownFaces;
          item.matches = result.matches || [];
          item.facesDetails = result.facesDetails || [];
          item.analyzed = true;

          totalFacesCount += result.totalFaces;

          // Inicializa assignedAtleta para cada rosto:
          // Auto-confirma APENAS se for Alta Certeza (status === 'confirmed' e confidence >= 80)
          const solidAthletes = [];
          (item.facesDetails || []).forEach((face) => {
            if (face.status === "confirmed" && face.bestMatch && face.bestMatch.confidence >= 80) {
              face.assignedAtleta = {
                _id: face.bestMatch.atletaId,
                nome: face.bestMatch.atletaNome,
              };
              solidAthletes.push(face.assignedAtleta);
            } else {
              face.assignedAtleta = null;
            }
          });

          const combined = [...item.confirmedAtletas];
          for (const det of solidAthletes) {
            if (!combined.some((a) => a._id === det._id)) {
              combined.push(det);
            }
          }
          item.confirmedAtletas = combined;

          if (item.matches.length > 0) {
            totalRecognizedCount += item.matches.length;
          }
        } catch (itemErr) {
          console.error(`Erro ao analisar ${item.name}:`, itemErr);
          item.analyzed = true;
        }

        setFileItems([...updatedItems]);
      }

      if (totalFacesCount === 0) {
        enqueueSnackbar(
          "Análise concluída: nenhum rosto nítido detectado nas fotos. Você pode marcar as atletas manualmente.",
          { variant: "info", autoHideDuration: 6000 }
        );
      } else if (totalRecognizedCount === 0) {
        enqueueSnackbar(
          `Foram detectados ${totalFacesCount} rostos, mas nenhum correspondeu às referências com confiança alta. Verifique as sugestões no painel de desambiguação!`,
          { variant: "warning", autoHideDuration: 7000 }
        );
      } else {
        enqueueSnackbar(
          `Reconhecimento concluído! ${totalFacesCount} rosto(s) detectado(s) e ${totalRecognizedCount} atleta(s) identificada(s). Revise e confirme abaixo.`,
          { variant: "success", autoHideDuration: 6000 }
        );
      }
    } catch (error) {
      console.error("Erro no reconhecimento facial:", error);
      enqueueSnackbar("Erro durante a análise de rostos: " + error.message, { variant: "error" });
    } finally {
      setIsAnalyzingIA(false);
      setAnalysisProgress({ current: 0, total: 0, text: "" });
    }
  };

  // Quando arquivos são selecionados
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Revoga apenas as URLs dos arquivos da seleção anterior
    createdUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (err) {
        // ignora se já revogado
      }
    });
    createdUrlsRef.current = [];

    const newItems = selectedFiles.map((file, idx) => {
      const url = URL.createObjectURL(file);
      createdUrlsRef.current.push(url);
      return {
        id: `${file.name}-${idx}-${Date.now()}`,
        file,
        name: file.name,
        previewUrl: url,
        status: "idle",
        totalFaces: 0,
        unknownFaces: 0,
        matches: [],
        facesDetails: [],
        confirmedAtletas: [...selectedAtletas],
        analyzed: false,
      };
    });

    setFileItems(newItems);
    setActiveFileIndex(0);
    setSuccessList([]);
    setErrorList([]);

    // Auto-dispara reconhecimento se houver atletas com biometria
    if (athletesWithFaces.length > 0) {
      setTimeout(() => {
        runRecognitionOnItems(newItems);
      }, 150);
    }
  };

  // Alterna a atribuição de atleta de um rosto específico
  // Garante que:
  // 1) Clicar em um card altera APENAS este rosto
  // 2) Uma mesma atleta NÃO pode ser atribuída a dois rostos diferentes na mesma foto
  const handleToggleFaceAssignment = (faceIndex, atleta) => {
    setFileItems((prev) => {
      const copy = [...prev];
      const active = copy[activeFileIndex];
      if (!active || !Array.isArray(active.facesDetails)) return prev;

      const face = active.facesDetails[faceIndex];
      if (!face) return prev;

      // Se este rosto já tinha esta atleta atribuída, desatribui apenas este rosto
      if (face.assignedAtleta && face.assignedAtleta._id === atleta._id) {
        face.assignedAtleta = null;
      } else {
        // Se outro rosto já tinha esta atleta, desatribui do outro rosto
        active.facesDetails.forEach((otherFace, idx) => {
          if (idx !== faceIndex && otherFace.assignedAtleta?._id === atleta._id) {
            otherFace.assignedAtleta = null;
          }
        });
        // Atribui exclusivamente a este rosto
        face.assignedAtleta = { _id: atleta._id, nome: atleta.nome };
      }

      // Sincroniza a lista de confirmedAtletas para esta foto
      const assignedFromFaces = active.facesDetails
        .map((f) => f.assignedAtleta)
        .filter(Boolean);

      const uniqueAssigned = [];
      const seenIds = new Set();
      for (const a of assignedFromFaces) {
        if (!seenIds.has(a._id)) {
          seenIds.add(a._id);
          uniqueAssigned.push(a);
        }
      }

      // Preserva atletas manuais que não vieram de rostos detectados
      const manualAthletes = (active.confirmedAtletas || []).filter(
        (manual) => !active.facesDetails.some((f) => f.bestMatch?.atletaId === manual._id)
      );
      for (const m of manualAthletes) {
        if (!seenIds.has(m._id)) {
          seenIds.add(m._id);
          uniqueAssigned.push(m);
        }
      }

      active.confirmedAtletas = uniqueAssigned;
      return copy;
    });
  };

  // Remove um atleta da lista de confirmados da foto atual
  const handleRemoveConfirmedAtleta = (atletaId) => {
    setFileItems((prev) => {
      const copy = [...prev];
      const active = copy[activeFileIndex];
      if (!active) return prev;

      if (Array.isArray(active.facesDetails)) {
        active.facesDetails.forEach((face) => {
          if (face.assignedAtleta?._id === atletaId) {
            face.assignedAtleta = null;
          }
        });
      }

      active.confirmedAtletas = (active.confirmedAtletas || []).filter((a) => a._id !== atletaId);
      return copy;
    });
  };

  // Alterna confirmação de um atleta específico (busca manual) para a foto ativa
  const handleToggleConfirmedAtleta = (atleta) => {
    setFileItems((prev) => {
      const copy = [...prev];
      const active = copy[activeFileIndex];
      if (!active) return prev;

      const exists = (active.confirmedAtletas || []).some((a) => a._id === atleta._id);
      if (exists) {
        handleRemoveConfirmedAtleta(atleta._id);
        return prev;
      } else {
        active.confirmedAtletas = [...(active.confirmedAtletas || []), { _id: atleta._id, nome: atleta.nome }];
      }
      return copy;
    });
  };

  // Aplica os atletas da foto ativa para todas as outras fotos do lote
  const handleApplyActiveToAll = () => {
    const active = fileItems[activeFileIndex];
    if (!active) return;

    if (active.confirmedAtletas.length === 0) {
      enqueueSnackbar("A foto ativa não possui nenhum atleta selecionado.", { variant: "info" });
      return;
    }

    setFileItems((prev) =>
      prev.map((item) => ({
        ...item,
        confirmedAtletas: [...active.confirmedAtletas],
      }))
    );

    enqueueSnackbar(
      `Atletas aplicados para todas as ${fileItems.length} fotos do lote!`,
      { variant: "success" }
    );
  };

  // Aceita todas as sugestões da IA em todo o lote
  const handleAcceptAllSuggestions = () => {
    setFileItems((prev) =>
      prev.map((item) => {
        const detected = (item.matches || []).map((m) => ({ _id: m.atletaId, nome: m.atletaNome }));
        const combined = [...item.confirmedAtletas];
        for (const det of detected) {
          if (!combined.some((a) => a._id === det._id)) {
            combined.push(det);
          }
        }
        return {
          ...item,
          confirmedAtletas: combined,
        };
      })
    );
    enqueueSnackbar("Todas as sugestões da IA foram confirmadas no lote!", {
      variant: "success",
    });
  };

  // Upload individual para o Cloudinary
  const uploadSingleFile = async (file) => {
    const data = new FormData();
    data.append("file", file);

    const uploadUrl = `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/upload-image`;
    const res = await axios.post(uploadUrl, data, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.results;
  };

  // Salvar produtos no backend
  const handleSaveProduct = async () => {
    if (!priceInCents || !evento) {
      enqueueSnackbar("Preencha todos os campos obrigatórios (Preço e Evento)", {
        variant: "warning",
      });
      return;
    }

    const price = parseInt(priceInCents);
    if (isNaN(price) || price <= 0) {
      enqueueSnackbar("O preço deve ser um número positivo em centavos", {
        variant: "warning",
      });
      return;
    }

    if (!fileItems || fileItems.length === 0) {
      enqueueSnackbar("Selecione uma ou mais imagens para upload", { variant: "warning" });
      return;
    }

    setLoading(true);
    setIsBatchUploading(true);
    setBatchProgress(0);
    setSuccessList([]);
    setErrorList([]);

    let successCount = 0;

    for (let i = 0; i < fileItems.length; i++) {
      const item = fileItems[i];
      setActiveFileIndex(i);

      // Define os atletas desta foto específica
      const atletasFoto =
        item.confirmedAtletas.length > 0 ? item.confirmedAtletas : selectedAtletas;

      const atletaIds = atletasFoto.map((a) => a._id);
      const atletaNomes = atletasFoto.map((a) => a.nome).join(", ");
      const atletasList = atletasFoto.map((a) => ({
        atletaId: a._id,
        atletaNome: a.nome,
      }));

      try {
        const uploadedImageUrls = await uploadSingleFile(item.file);
        const { image, imageSample } = uploadedImageUrls;

        const formData = {
          local: local || item.name,
          priceInCents,
          tags,
          image,
          imageSample,
          evento,
          atletaId: atletaIds[0] || null,
          atletaNome: atletaNomes || null,
          atletasIds: atletaIds,
          atletas: atletasList,
        };

        await axios.post(
          `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/product`,
          formData,
          config
        );

        setSuccessList((prev) => [...prev, item.name]);
        successCount += 1;
      } catch (error) {
        console.error(`Erro ao salvar ${item.name}:`, error);
        setErrorList((prev) => [
          ...prev,
          { name: item.name, message: error.response?.data?.message || error.message },
        ]);
      } finally {
        setBatchProgress((prev) => prev + 1);
      }
    }

    setIsBatchUploading(false);
    setLoading(false);

    enqueueSnackbar(
      `Upload concluído: ${successCount}/${fileItems.length} foto(s) salva(s) com sucesso!`,
      { variant: "success" }
    );
  };

  const activeItem = fileItems[activeFileIndex] || null;

  // Filtragem de atletas para busca rápida na desambiguação
  const disambigFilteredAtletas = useMemo(() => {
    if (!disambigSearchTerm.trim()) return atletas.slice(0, 8);
    const q = disambigSearchTerm.toLowerCase();
    return atletas
      .filter((a) => a.nome.toLowerCase().includes(q))
      .slice(0, 10);
  }, [atletas, disambigSearchTerm]);

  return (
    <div className="p-4 sm:p-6 bg-base-100 flex flex-col lg:flex-row gap-6 justify-center items-start mt-14 max-w-7xl mx-auto">
      {loading && <Spinner />}

      {/* Painel Esquerdo: Formulário de Cadastro e Seleção de Fotos */}
      <div className="w-full lg:w-96 shadow-lg rounded-xl p-5 bg-base-100 border border-base-200">
        <h1 className="text-2xl font-bold mb-4 text-base-content">Upload de Fotos</h1>

        <div className="space-y-4">
          <div>
            <label htmlFor="evento" className="block text-sm font-semibold mb-1">
              Evento*
            </label>
            <input
              id="evento"
              type="text"
              value={evento}
              onChange={(e) => setEvento(e.target.value)}
              placeholder="Ex: Campeonato Estadual 2026"
              className="border border-base-300 px-3 py-2 w-full rounded-lg text-sm bg-base-100 focus:outline-primary"
              required
            />
          </div>

          <div>
            <label htmlFor="local" className="block text-sm font-semibold mb-1">
              Local
            </label>
            <input
              id="local"
              type="text"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="Ex: Ginásio Ibirapuera"
              className="border border-base-300 px-3 py-2 w-full rounded-lg text-sm bg-base-100 focus:outline-primary"
            />
          </div>

          <div>
            <label htmlFor="priceInCents" className="block text-sm font-semibold mb-1">
              Preço em centavos*
            </label>
            <input
              id="priceInCents"
              type="number"
              value={priceInCents}
              onChange={(e) => setPriceInCents(e.target.value)}
              placeholder="Ex: 1500 (R$ 15,00)"
              className="border border-base-300 px-3 py-2 w-full rounded-lg text-sm bg-base-100 focus:outline-primary"
              required
            />
          </div>

          <div>
            <label htmlFor="tags" className="block text-sm font-semibold mb-1">
              Tags
            </label>
            <input
              id="tags"
              type="text"
              value={tags}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: final, vôlei, saque"
              className="border border-base-300 px-3 py-2 w-full rounded-lg text-sm bg-base-100 focus:outline-primary"
            />
          </div>

          {/* Seleção de fotos */}
          <div className="pt-2 border-t border-base-200">
            <label className="block text-sm font-semibold mb-1">
              Fotos para Upload*
            </label>
            <input
              id="imgFull"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input file-input-bordered file-input-primary file-input-sm w-full"
              multiple
              ref={imgFullRef}
            />
            {fileItems.length > 0 && (
              <p className="text-xs text-primary font-medium mt-1">
                ✓ {fileItems.length} foto(s) selecionada(s)
              </p>
            )}
          </div>

          {/* Informações da IA de Reconhecimento Facial */}
          <div className="border border-primary/30 bg-primary/5 rounded-xl p-3.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-primary flex items-center gap-1.5">
                <span>🤖</span> Reconhecimento Facial IA
              </span>
              <span className="badge badge-sm badge-outline badge-primary text-[10px]">
                {athletesWithFaces.length} atletas c/ biometria
              </span>
            </div>

            <p className="text-xs opacity-75 leading-relaxed">
              Compara os rostos nas fotos contra as biometrias cadastradas. Análise 100% no navegador, sem custos.
            </p>

            <button
              type="button"
              disabled={isAnalyzingIA || fileItems.length === 0 || athletesWithFaces.length === 0}
              onClick={() => runRecognitionOnItems()}
              className={`btn btn-sm btn-primary w-full ${isAnalyzingIA ? "loading" : ""}`}
            >
              {isAnalyzingIA ? "Analisando Rostos..." : "⚡ Identificar Atletas com IA"}
            </button>

            {athletesWithFaces.length === 0 && (
              <p className="text-[11px] text-warning font-medium">
                ⚠️ Cadastre fotos de rosto em &quot;Editar Atleta&quot; para ativar o reconhecimento automático.
              </p>
            )}
          </div>

          {/* Atletas padrão para aplicar a todas as fotos (opcional) */}
          <div className="border border-base-300 rounded-xl p-3 bg-base-200/40">
            <label className="block text-sm font-medium mb-1">
              Atletas padrão para o lote <span className="text-xs font-normal opacity-70">(opcional)</span>
            </label>
            <p className="text-xs opacity-70 mb-2">
              Selecione atletas para marcar como padrão em todas as fotos.
            </p>

            {selectedAtletas.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedAtletas.map((a) => (
                  <span key={a._id} className="badge badge-primary badge-sm gap-1 py-2 px-2 text-xs">
                    {a.nome}
                    <button
                      type="button"
                      onClick={() => handleToggleAtleta(a)}
                      className="hover:text-red-300 ml-1 font-bold"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}

            <input
              id="atleta-search"
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Filtrar atletas..."
              className="border border-base-300 px-3 py-1.5 w-full rounded-md text-xs mb-2 bg-base-100"
            />

            <div className="max-h-32 overflow-y-auto border border-base-300 rounded-md p-1.5 bg-base-100 divide-y divide-base-200">
              {filteredAtletas.length > 0 ? (
                filteredAtletas.map((atleta) => {
                  const isChecked = selectedAtletas.some((a) => a._id === atleta._id);
                  return (
                    <label
                      key={atleta._id}
                      className="flex items-center gap-2 py-1 px-1 hover:bg-base-200 rounded cursor-pointer text-xs"
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-xs checkbox-primary"
                        checked={isChecked}
                        onChange={() => handleToggleAtleta(atleta)}
                      />
                      <span className="font-medium truncate">{atleta.nome}</span>
                      {atleta.fotosReferencia?.length > 0 && (
                        <span className="badge badge-ghost badge-xs text-[9px] ml-auto">
                          IA
                        </span>
                      )}
                    </label>
                  );
                })
              ) : (
                <div className="text-xs text-center py-2 opacity-60">Nenhum atleta encontrado</div>
              )}
            </div>
          </div>

          <button
            onClick={handleSaveProduct}
            disabled={loading || fileItems.length === 0}
            className="w-full bg-green-600 hover:bg-green-700 font-semibold text-white py-2.5 px-4 rounded-xl shadow-md transition-all mt-4 disabled:opacity-50"
          >
            {loading ? "Salvando..." : `Salvar ${fileItems.length > 0 ? `(${fileItems.length} fotos)` : ""}`}
          </button>

          <Link to="/admin" className="btn btn-outline btn-sm w-full mt-2">
            Voltar
          </Link>
        </div>
      </div>

      {/* Painel Direito: Preview e Desambiguação de Atletas */}
      <div className="flex-1 w-full shadow-lg rounded-xl p-5 bg-base-100 border border-base-200 min-h-[600px] flex flex-col">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-3 border-b border-base-200 gap-2">
          <div>
            <h2 className="text-xl font-bold text-base-content flex items-center gap-2">
              <span>🖼️</span> Preview & Desambiguação Facial
            </h2>
            <p className="text-xs opacity-70">
              Revise os rostos detectados em cada foto e confirme ou ajuste as atletas sugeridas.
            </p>
          </div>

          {fileItems.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAcceptAllSuggestions}
                className="btn btn-xs btn-outline btn-success"
                title="Aceita todas as sugestões da IA para todas as fotos do lote"
              >
                ✓ Aceitar Tudo da IA
              </button>
              <button
                type="button"
                onClick={handleApplyActiveToAll}
                className="btn btn-xs btn-outline btn-info"
                title="Aplica as atletas desta foto para todas as outras fotos do lote"
              >
                Duplicar Atletas P/ Todas
              </button>
            </div>
          )}
        </div>

        {/* Barra de Progresso de Análise da IA */}
        {isAnalyzingIA && (
          <div className="my-4 p-3.5 bg-primary/10 rounded-xl border border-primary/20 animate-pulse">
            <div className="flex justify-between text-xs font-semibold text-primary mb-1.5">
              <span>{analysisProgress.text}</span>
              <span>
                {analysisProgress.current}/{analysisProgress.total}
              </span>
            </div>
            <progress
              className="progress progress-primary w-full"
              value={analysisProgress.current}
              max={analysisProgress.total}
            ></progress>
          </div>
        )}

        {/* Carrossel / Fila de Miniaturas em Lote */}
        {fileItems.length > 1 && (
          <div className="my-3">
            <div className="flex items-center justify-between text-xs font-medium mb-1.5 opacity-80">
              <span>Fotos do Lote ({fileItems.length}):</span>
              <span>Foto ativa: {activeFileIndex + 1} de {fileItems.length}</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {fileItems.map((item, idx) => {
                const isActive = idx === activeFileIndex;
                const athleteCount = item.confirmedAtletas.length;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveFileIndex(idx)}
                    className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all group ${
                      isActive
                        ? "border-primary shadow-md scale-105"
                        : "border-base-300 opacity-75 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={item.previewUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1 right-1">
                      {athleteCount > 0 ? (
                        <span className="badge badge-success badge-xs font-bold text-[9px] px-1">
                          {athleteCount}
                        </span>
                      ) : (
                        <span className="badge badge-ghost badge-xs text-[9px] px-1 opacity-70">
                          0
                        </span>
                      )}
                    </div>
                    {item.analyzed && (
                      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-[8px] text-white text-center py-0.5 truncate">
                        {item.matches?.length > 0 ? `🤖 ${item.matches.length}` : `${item.totalFaces || 0} rostos`}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Conteúdo Principal da Foto Ativa */}
        {activeItem ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4 flex-1">
            {/* Foto Ativa */}
            <div className="flex flex-col bg-base-200/50 rounded-xl p-3 border border-base-200">
              <div className="w-full flex items-center justify-between text-xs opacity-75 mb-2 px-1 pb-2 border-b border-base-300/60">
                <span className="font-mono truncate max-w-[200px] sm:max-w-[280px]" title={activeItem.name}>
                  {activeItem.name}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11px] font-mono opacity-80 mr-1">
                    {activeFileIndex + 1} / {fileItems.length}
                  </span>
                  <button
                    type="button"
                    disabled={activeFileIndex === 0}
                    onClick={() => setActiveFileIndex((prev) => Math.max(0, prev - 1))}
                    className="btn btn-ghost btn-xs"
                  >
                    ◀ Anterior
                  </button>
                  <button
                    type="button"
                    disabled={activeFileIndex >= fileItems.length - 1}
                    onClick={() =>
                      setActiveFileIndex((prev) => Math.min(fileItems.length - 1, prev + 1))
                    }
                    className="btn btn-ghost btn-xs"
                  >
                    Próxima ▶
                  </button>
                </div>
              </div>
              <div className="relative h-[460px] w-full flex items-center justify-center overflow-hidden rounded-lg bg-base-300/10">
                <img
                  src={activeItem.previewUrl}
                  alt={activeItem.name}
                  className="max-h-[440px] max-w-full object-contain rounded-md shadow-md"
                />
              </div>
            </div>

            {/* Painel de Desambiguação / Atletas da Foto */}
            <div className="flex flex-col space-y-4">
              {/* Status do Reconhecimento */}
              <div className="border border-base-300 rounded-xl p-3.5 bg-base-200/30">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-bold flex items-center gap-1.5">
                    <span>🔍</span> Diagnóstico Facial
                  </span>
                  {activeItem.analyzed ? (
                    <span className="badge badge-sm badge-success font-medium">Analisada</span>
                  ) : (
                    <span className="badge badge-sm badge-ghost text-xs">Pendente</span>
                  )}
                </div>

                {activeItem.analyzed ? (
                  <div className="text-xs space-y-1">
                    <p>
                      Total de rostos detectados:{" "}
                      <span className="font-bold">{activeItem.totalFaces}</span>
                    </p>
                    <p>
                      Atletas sugeridas:{" "}
                      <span className="font-bold text-success">
                        {activeItem.matches?.length || 0}
                      </span>
                    </p>
                    {activeItem.unknownFaces > 0 && (
                      <p className="text-warning">
                        ⚠️ {activeItem.unknownFaces} rosto(s) com menor certeza ou sem cadastro prévio.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs opacity-70">
                    Clique no botão &quot;Identificar Atletas com IA&quot; para escanear os rostos desta foto.
                  </p>
                )}
              </div>

              {/* Rostos Detectados com Fotos Recortadas (Desambiguação Humana Visual) */}
              {activeItem.facesDetails && activeItem.facesDetails.length > 0 && (
                <div className="border border-primary/30 rounded-xl p-4 bg-primary/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-primary flex items-center gap-1.5">
                      <span>📸</span> Rostos Encontrados na Foto ({activeItem.facesDetails.length})
                    </span>
                    <span className="text-[10px] opacity-70">Desambiguação visual</span>
                  </div>

                  <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                    {activeItem.facesDetails.map((face) => {
                      const best = face.bestMatch;
                      const isThisFaceAssigned =
                        best &&
                        face.assignedAtleta &&
                        face.assignedAtleta._id === best.atletaId;

                      return (
                        <div
                          key={face.faceIndex}
                          className={`flex items-center gap-2.5 p-2 rounded-lg border transition-all ${
                            isThisFaceAssigned
                              ? "bg-primary/5 border-primary shadow-sm"
                              : "bg-base-100 border-base-300"
                          }`}
                        >
                          {/* Miniatura do Rosto Recortado da Própria Foto */}
                          {face.faceCropUrl && (
                            <img
                              src={face.faceCropUrl}
                              alt={`Rosto ${face.faceIndex + 1}`}
                              className="w-12 h-12 object-cover rounded-md border border-base-300 flex-shrink-0"
                            />
                          )}

                          <div className="flex-1 min-w-0">
                            {best ? (
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-xs truncate">
                                    {best.atletaNome}
                                  </span>
                                  <span
                                    className={`badge badge-xs text-[9px] font-bold ${
                                      best.confidence >= 75
                                        ? "badge-success text-white"
                                        : "badge-info text-white"
                                    }`}
                                  >
                                    {best.confidence}%
                                  </span>
                                </div>
                                <div className="text-[10px] opacity-70">
                                  dist: {best.distance.toFixed(3)} •{" "}
                                  {face.status === "confirmed" ? "Alta certeza" : "Sugestão"}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="font-semibold text-xs text-warning">
                                  Rosto não identificado
                                </div>
                                <div className="text-[10px] opacity-60">
                                  Rosto #{face.faceIndex + 1}
                                </div>
                              </div>
                            )}

                            {/* Alternativas se houver */}
                            {face.topCandidates && face.topCandidates.length > 1 && (
                              <div className="text-[9px] opacity-65 truncate mt-0.5">
                                Opções:{" "}
                                {face.topCandidates
                                  .slice(1, 3)
                                  .map((c) => `${c.atletaNome} (${c.confidence}%)`)
                                  .join(", ")}
                              </div>
                            )}
                          </div>

                          {/* Botão de confirmação específico deste rosto */}
                          {best && (
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleFaceAssignment(face.faceIndex, {
                                  _id: best.atletaId,
                                  nome: best.atletaNome,
                                })
                              }
                              className={`btn btn-xs ${
                                isThisFaceAssigned
                                  ? "btn-success"
                                  : "btn-outline btn-primary"
                              }`}
                            >
                              {isThisFaceAssigned ? "✓ Confirmada" : "+ Confirmar"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Atletas Confirmados nesta Foto */}
              <div className="border border-base-300 rounded-xl p-4 bg-base-100 flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold">
                    Atletas Confirmadas nesta Foto ({activeItem.confirmedAtletas.length})
                  </span>
                  {activeItem.confirmedAtletas.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setFileItems((prev) => {
                          const copy = [...prev];
                          const active = copy[activeFileIndex];
                          if (active) {
                            active.confirmedAtletas = [];
                            if (Array.isArray(active.facesDetails)) {
                              active.facesDetails.forEach((f) => (f.assignedAtleta = null));
                            }
                          }
                          return copy;
                        })
                      }
                      className="text-xs text-red-500 hover:underline"
                    >
                      Limpar todas
                    </button>
                  )}
                </div>

                {activeItem.confirmedAtletas.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {activeItem.confirmedAtletas.map((a) => (
                      <span
                        key={a._id}
                        className="badge badge-primary gap-1 py-3 px-2.5 text-xs font-semibold shadow-sm"
                      >
                        {a.nome}
                        <button
                          type="button"
                          onClick={() => handleRemoveConfirmedAtleta(a._id)}
                          className="hover:text-red-300 ml-1 font-bold"
                          title="Remover atleta desta foto"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-center py-3 bg-base-200/50 rounded-lg border border-dashed border-base-300 mb-3 opacity-70">
                    Nenhuma atleta marcada para esta foto ainda.
                  </div>
                )}

                {/* Adicionar / Buscar manualmente outro atleta nesta foto */}
                <div className="pt-2 border-t border-base-200">
                  <label className="block text-xs font-medium mb-1 opacity-80">
                    Adicionar outra atleta manualmente nesta foto:
                  </label>
                  <input
                    type="text"
                    value={disambigSearchTerm}
                    onChange={(e) => setDisambigSearchTerm(e.target.value)}
                    placeholder="Buscar atleta por nome..."
                    className="border border-base-300 px-3 py-1.5 w-full rounded-md text-xs mb-2 bg-base-100 focus:outline-primary"
                  />

                  <div className="max-h-28 overflow-y-auto border border-base-300 rounded-md p-1 bg-base-100 divide-y divide-base-200">
                    {disambigFilteredAtletas.map((atleta) => {
                      const isConfirmed = activeItem.confirmedAtletas.some(
                        (a) => a._id === atleta._id
                      );
                      return (
                        <div
                          key={atleta._id}
                          onClick={() => handleToggleConfirmedAtleta(atleta)}
                          className="flex items-center justify-between p-1.5 hover:bg-base-200 rounded cursor-pointer text-xs"
                        >
                          <span className="font-medium truncate">{atleta.nome}</span>
                          <button
                            type="button"
                            className={`btn btn-xs ${
                              isConfirmed ? "btn-error btn-outline" : "btn-primary btn-outline"
                            }`}
                          >
                            {isConfirmed ? "Remover" : "+ Marcar"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-center opacity-60">
            <span className="text-6xl mb-3">📷</span>
            <p className="text-lg font-semibold">Nenhuma foto selecionada</p>
            <p className="text-sm max-w-sm mt-1">
              Escolha uma ou mais fotos no painel à esquerda para visualizar o preview e utilizar o
              reconhecimento facial com IA.
            </p>
          </div>
        )}

        {/* Progresso de Upload para o Backend */}
        {(isBatchUploading || batchProgress > 0) && (
          <div className="mt-4 p-4 bg-base-200 rounded-xl border border-base-300">
            <div className="flex justify-between text-sm font-semibold mb-2">
              <span>Progresso do Upload no Servidor:</span>
              <span>
                {batchProgress}/{fileItems.length}
              </span>
            </div>
            <progress
              className="progress progress-primary w-full"
              value={batchProgress}
              max={fileItems.length}
            ></progress>

            {errorList.length > 0 && (
              <div className="mt-4 p-3 bg-red-100/50 dark:bg-red-950/30 rounded-lg text-red-600">
                <div className="font-semibold text-sm">Erros ({errorList.length}):</div>
                <ul className="text-xs mt-1 space-y-0.5">
                  {errorList.map((e, idx) => (
                    <li key={idx}>
                      - {e.name}: {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {successList.length > 0 && (
              <div className="mt-4 p-3 bg-green-100/50 dark:bg-green-950/30 rounded-lg text-green-700 dark:text-green-300">
                <div className="font-semibold text-sm">Sucesso ({successList.length}):</div>
                <ul className="text-xs mt-1 space-y-0.5">
                  {successList.map((n, idx) => (
                    <li key={idx}>- {n}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProduct;
